// The Notifications page's plain-language names, as closed records of QUOTED
// key literals.
//
// Closed, so a kind or a tier added to the domain without a row here is a
// compile error rather than a `⟦key⟧` on the page. Quoted literals, so
// `i18n.coverage.test.ts` can read each key off this file and check that it
// resolves; a template-literal key would escape it. A kind id or a tier id is
// never shown to a person: these labels are what they read instead
// (`notifications-copy.test.ts` refuses the platform's words in them).

import type { NotificationKind, NotificationPreset, NotificationTier } from '@developerz/domain';

/** Every kind the page lists. `platform_notice` is the hidden tier: it has no row. */
export type ShownKind = Exclude<NotificationKind, 'platform_notice'>;

/** Every tier the page draws a card for. */
export type ShownTier = Exclude<NotificationTier, 'hidden'>;

export const KIND_LABEL_KEY: Record<ShownKind, string> = {
  escalation: 'notifications_settings.topic.escalation',
  decision_needed: 'notifications_settings.topic.decision_needed',
  task_blocked: 'notifications_settings.topic.task_blocked',
  onboarding_wait: 'notifications_settings.topic.onboarding_wait',
  box_offline: 'notifications_settings.topic.box_offline',
  key_outage: 'notifications_settings.topic.key_outage',
  no_model_keys: 'notifications_settings.topic.no_model_keys',
  share_link: 'notifications_settings.topic.share_link',
  pr_merged: 'notifications_settings.topic.pr_merged',
  ci_failed: 'notifications_settings.topic.ci_failed',
  bug_scouted: 'notifications_settings.topic.bug_scouted',
  ask_duplicate: 'notifications_settings.topic.ask_duplicate',
  backlog_imported: 'notifications_settings.topic.backlog_imported',
  good_news: 'notifications_settings.topic.good_news',
  ops_digest: 'notifications_settings.topic.ops_digest',
  // Not in the issue's table: `coding_paused` (#5647) shipped after the table
  // was written, and its tier is heads-up, so the closed record demands a row.
  coding_paused: 'notifications_settings.topic.coding_paused',
  lane_degraded: 'notifications_settings.topic.lane_degraded',
  weekly_summary: 'notifications_settings.topic.weekly_summary',
};

export const TIER_LABEL_KEY: Record<ShownTier, string> = {
  urgent: 'notifications_settings.tier.urgent',
  decision: 'notifications_settings.tier.decision',
  heads_up: 'notifications_settings.tier.heads_up',
  updates: 'notifications_settings.tier.updates',
  summaries: 'notifications_settings.tier.summaries',
};

/** One plain sentence saying what the tier is for. */
export const TIER_DESC_KEY: Record<ShownTier, string> = {
  urgent: 'notifications_settings.tier.urgent_desc',
  decision: 'notifications_settings.tier.decision_desc',
  heads_up: 'notifications_settings.tier.heads_up_desc',
  updates: 'notifications_settings.tier.updates_desc',
  summaries: 'notifications_settings.tier.summaries_desc',
};

/** One example of what arrives in the tier. */
export const TIER_EXAMPLE_KEY: Record<ShownTier, string> = {
  urgent: 'notifications_settings.tier.urgent_example',
  decision: 'notifications_settings.tier.decision_example',
  heads_up: 'notifications_settings.tier.heads_up_example',
  updates: 'notifications_settings.tier.updates_example',
  summaries: 'notifications_settings.tier.summaries_example',
};

export const PRESET_LABEL_KEY: Record<NotificationPreset, string> = {
  only_urgent: 'notifications_settings.preset.only_urgent',
  recommended: 'notifications_settings.preset.recommended',
  everything: 'notifications_settings.preset.everything',
};

/** What the current choice means. `custom` is "the switches match no preset". */
export const PRESET_DESC_KEY: Record<NotificationPreset | 'custom', string> = {
  only_urgent: 'notifications_settings.preset.current_only_urgent',
  recommended: 'notifications_settings.preset.current_recommended',
  everything: 'notifications_settings.preset.current_everything',
  custom: 'notifications_settings.preset.current_custom',
};
